import { isNavToolFeatureEnabled, type NavToolMenuItem } from '@/components/NavMenus/navMenuConfig';

export type ToolAccessStatus = 'available' | 'upgrade' | 'login' | 'paid' | 'hidden';

export type ToolAccessContext = {
  isAuthenticated: boolean;
  groups: string[];
};

export type ToolAccessResult = {
  status: ToolAccessStatus;
  reason?: string;
  upgradeHref: string;
};

export const TOOL_PLANS_HREF = '/plans';
export const TOOL_LOGIN_HREF = '/login';

const DOWNLOAD_ROLES = ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'] as const;

const API_FAMILY_ROLES = [
  'ROLE_ACADEMIC',
  'ROLE_TRIAL',
  'ROLE_PROFESSIONAL',
  'ROLE_UNLIMITED',
  'ROLE_DEVELOPER',
] as const;

const CYTOSCAPE_ROLES = ['ROLE_TRIAL', 'ROLE_PROFESSIONAL', 'ROLE_UNLIMITED', 'ROLE_DEVELOPER'] as const;

const AIDA_ROLES = [
  'ROLE_TRIAL',
  'ROLE_STANDARD',
  'ROLE_PROFESSIONAL',
  'ROLE_UNLIMITED',
  'ROLE_DEVELOPER',
] as const;

function hasAnyRole(groups: string[], roles: readonly string[]): boolean {
  return roles.some(role => groups.includes(role));
}

function denied(ctx: ToolAccessContext, reason: string): ToolAccessResult {
  if (!ctx.isAuthenticated) {
    return { status: 'login', reason, upgradeHref: TOOL_LOGIN_HREF };
  }
  return { status: 'upgrade', reason, upgradeHref: TOOL_PLANS_HREF };
}

function available(): ToolAccessResult {
  return { status: 'available', upgradeHref: TOOL_PLANS_HREF };
}

export function getToolAccess(toolId: string, ctx: ToolAccessContext): ToolAccessResult {
  const groups = ctx.groups ?? [];

  switch (toolId) {
    case 'download': {
      if (hasAnyRole(groups, DOWNLOAD_ROLES)) return available();
      return {
        status: 'hidden',
        reason: 'Download is only available on Unlimited plans.',
        upgradeHref: TOOL_PLANS_HREF,
      };
    }

    case 'ai': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use AIDA.');
      if (hasAnyRole(groups, AIDA_ROLES)) return available();
      return denied(ctx, 'AIDA is not included in the free Academic plan.');
    }

    case 'api': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use the REST API console.');
      if (hasAnyRole(groups, API_FAMILY_ROLES)) return available();
      return denied(ctx, 'REST API access is not included in the Standard plan.');
    }

    case 'r-package': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use the R package.');
      if (hasAnyRole(groups, API_FAMILY_ROLES)) return available();
      return denied(ctx, 'The R package is not included in the Standard plan.');
    }

    case 'mcp': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use the MCP connector.');
      if (hasAnyRole(groups, API_FAMILY_ROLES)) return available();
      return denied(ctx, 'MCP access is not included in the Standard plan.');
    }

    case 'cytoscape': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use the Cytoscape app.');
      if (hasAnyRole(groups, CYTOSCAPE_ROLES)) return available();
      return denied(ctx, 'Cytoscape access requires Trial, Advanced, or Unlimited.');
    }

    case 'enrichment':
    case 'advanced-search':
    case 'geo-browse':
    case 'ontology-explorer': {
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use this tool.');
      return available();
    }

    case 'gsea-ranked':
      return { status: 'paid', upgradeHref: TOOL_PLANS_HREF, reason: 'GSEA is a paid add-on.' };

    default:
      if (!ctx.isAuthenticated) return denied(ctx, 'Sign in to use this tool.');
      return available();
  }
}

export function canUseTool(toolId: string, ctx: ToolAccessContext): boolean {
  return getToolAccess(toolId, ctx).status === 'available';
}

export function isDownloadDumpAllowed(groups: string[] | undefined | null): boolean {
  return hasAnyRole(groups ?? [], DOWNLOAD_ROLES);
}

export function isToolCatalogVisible(tool: NavToolMenuItem, ctx: ToolAccessContext): boolean {
  if (!isNavToolFeatureEnabled(tool)) return false;
  return getToolAccess(tool.id, ctx).status !== 'hidden';
}

export function toolAccessNeedsUpgrade(status: ToolAccessStatus): boolean {
  return status === 'upgrade' || status === 'login';
}

export function buildToolAccessContext(
  isAuthenticated: boolean | undefined,
  groups: string[] | undefined | null,
): ToolAccessContext {
  return {
    isAuthenticated: Boolean(isAuthenticated),
    groups: groups ?? [],
  };
}
